/** Vendors / Supplier requests - what this workspace asked its suppliers, and what came back (ADR-0040,
 * ADR-0212). The buyer's side; a supplier's side is their own workspace.
 *
 * **A supplier is not a vendor.** A supplier is another company's tenant that answers requests and
 * shares under an agreement; names never cross, so a row shows the request (fuel or carrier, category,
 * period) and the workspace's short id, never a company name. The info control beside the title says
 * the difference in one place.
 *
 * Tiles count the rows the server sent (open, answered, overdue); nothing is summed. A row opens the
 * exchange in a drawer with its answer and, for an Approver, the decision to copy it into the
 * inventory (`ExchangeCard`, unchanged: every refusal and every sentence about a grade-C copy stays).
 * Request data, Permissions, Responsiveness and Purchases are the same panels the supplier exchange
 * always had, each in its own drawer, so none of it is lost to the table page.
 */

import { Handshake } from "@phosphor-icons/react";
import { useState } from "react";

import { useMe } from "../../api/auth";
import { useFacilities } from "../../api/evidence";
import { useAgreements, useExchange } from "../../api/suppliers";
import type { Exchange } from "../../api/suppliers";
import { ReadFailed } from "../../components/Loaded";
import { Drawer } from "../../components/ui/Drawer";
import { EntityLink } from "../../components/ui/EntityLink";
import { NotYourRole } from "../../components/NotYourRole";
import { EmptyAction } from "../../components/ui/EmptyAction";
import { EmptyState } from "../../components/ui/EmptyState";
import { FilterLine } from "../../components/ui/FilterLine";
import { EMPTY_FILTERS } from "../../components/ui/filters";
import type { FilterDef, FilterState } from "../../components/ui/filters";
import { GridTable } from "../../components/ui/GridTable";
import type { GridColumn } from "../../components/ui/GridTable";
import { InfoPopover } from "../../components/ui/InfoPopover";
import type { SortState } from "../../components/ui/sortRows";
import { StatusChip } from "../../components/ui/StatusChip";
import { TablePage } from "../../components/ui/TablePage";
import { Tile, TileStrip } from "../../components/ui/Tile";
import { shortDate } from "../../lib/format";
import { ProcurementPanels } from "../Procurement";
import { ExchangeCard, PermissionsPanel, SupplierResponsivenessPanel, needsYou, stateTone } from "../Suppliers";
import { RaiseRequest } from "../SupplierRaise";
import { SupplierExchangeOverview } from "../SupplierExchangeOverview";
import { stateLabel, supplierName, supplierNumbers } from "../supplierLabels";
import { keepSearch } from "../search";
import { count, matches, ordered, usePlaceLabel } from "../tableKit";
import { useScreen } from "../useScreen";
import { MAY_COLLECT, MAY_RECORD } from "./roles";
import styles from "./Vendors.module.css";

type Panel = "request" | "census" | "permissions" | "responsiveness" | "purchases";


function title(row: Exchange): string {
  return `${row.fuel_or_carrier.replace(/_/g, " ")} · ${row.category.replace(/_/g, " ")}`;
}

export function RequestsTab() {
  const labelText = usePlaceLabel();
  const screen = useScreen();
  const role = useMe().data?.role;
  const exchange = useExchange();
  const agreements = useAgreements();
  const facilities = useFacilities();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortState | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel | null>(null);

  const mayCollect = role === undefined ? undefined : MAY_COLLECT.includes(role);
  const mayDecide = role === undefined ? undefined : MAY_RECORD.includes(role);
  const rows = exchange.data ?? [];
  const numbers = supplierNumbers(rows);
  const sites = new Map((facilities.data ?? []).map((site) => [site.id, site.name]));
  const todayIso = new Date().toISOString().slice(0, 10);
  const overdue = rows.filter((r) => r.state === "open" && r.due_on !== null && r.due_on < todayIso);

  const defs: FilterDef[] = [
    {
      id: "state",
      label: "State",
      mode: "single",
      options: [...new Set(rows.map((r) => r.state))].map((state) => ({ value: state, label: stateLabel(state) })),
    },
    {
      id: "category",
      label: "Category",
      mode: "single",
      options: [...new Set(rows.map((r) => r.category))].map((c) => ({ value: c, label: c.replace(/_/g, " ") })),
    },
  ];
  const state = filters.values.state?.[0];
  const category = filters.values.category?.[0];
  const shown = ordered(
    rows.filter(
      (r) =>
        (!state || r.state === state) &&
        (!category || r.category === category) &&
        matches(filters.search, title(r), supplierName(r.supplier_org_id, numbers)),
    ),
    sort,
    { request: title, period: (r) => r.period_start, due: (r) => r.due_on, state: (r) => r.state },
  );

  const columns: GridColumn<Exchange>[] = [
    { id: "request", header: "Request", sortable: true, cell: title },
    {
      id: "supplier",
      header: "Supplier",
      cell: (r) =>
        r.i_am === "buyer" ? (
          /* The way into the supplier's account page (EXP-14): a real link, so it can be followed in a
             new tab. Only where this workspace is the buyer: on the other side the counterparty is a
             customer, and the account page is about a supplier. */
          <EntityLink
            kind="supplier"
            to="/suppliers/$supplierId"
            params={{ supplierId: r.supplier_org_id }}
            search={{ ...keepSearch(screen) }}
            announceKind={false}
            testId={`supplier-link-${r.id}`}
          >
            {supplierName(r.supplier_org_id, numbers)}
          </EntityLink>
        ) : (
          "Asked of you"
        ),
    },
    { id: "site", header: "Site", cell: (r) => (r.facility_id ? (sites.get(r.facility_id) ?? "—") : "—") },
    {
      id: "period",
      header: "Period",
      sortable: true,
      cell: (r) => `${shortDate(r.period_start)} – ${shortDate(r.period_end)}`,
    },
    { id: "due", header: "Due", sortable: true, cell: (r) => (r.due_on ? shortDate(r.due_on) : "—") },
    {
      id: "state",
      header: "State",
      sortable: true,
      cell: (r) => {
        const tone = stateTone(r, { mayCollect, mayDecide });
        return (
          <StatusChip tone={tone}>{needsYou(r, { mayCollect, mayDecide }) ?? stateLabel(r.state)}</StatusChip>
        );
      },
    },
  ];

  const opened = rows.find((r) => r.id === open);
  const close = () => setPanel(null);

  return (
    <>
      <TablePage
        icon={Handshake}
        title="Vendors"
        label={labelText}
        actions={
          <>
            <InfoPopover infoKey="vendors.supplier-requests" />
            {mayCollect ? (
              <button type="button" className={styles.primary} onClick={() => setPanel("request")}>
                Request data
              </button>
            ) : null}
          </>
        }
        extra={
          mayCollect === false ? <NotYourRole action="requestSupplierData" testId="requests-who-can" /> : undefined
        }
        tiles={
          exchange.data ? (
            <TileStrip label="Supplier requests summary">
              <Tile value={count(rows.filter((r) => r.state === "open").length)} label="Open" />
              <Tile value={count(rows.filter((r) => r.state === "answered").length)} label="Answered" />
              <Tile value={count(overdue.length)} label="Overdue" />
            </TileStrip>
          ) : undefined
        }
        filters={
          <FilterLine
            filters={defs}
            state={filters}
            onChange={setFilters}
            searchLabel="Search requests"
            resultCount={exchange.data ? { shown: shown.length, total: rows.length, noun: "requests" } : undefined}
            actions={
              <>
                {/* With nothing asked yet there is no census, no permission and no response time to
                    look at: those three are drawn once a request exists. Purchases is the spend
                    import and stands without one. */}
                {rows.length > 0 ? (
                  <>
                    <button type="button" className={styles.ghost} onClick={() => setPanel("census")}>
                      Expected bills
                    </button>
                    <button type="button" className={styles.ghost} onClick={() => setPanel("permissions")}>
                      Permissions
                    </button>
                    <button type="button" className={styles.ghost} onClick={() => setPanel("responsiveness")}>
                      Responsiveness
                    </button>
                  </>
                ) : null}
                <button type="button" className={styles.ghost} onClick={() => setPanel("purchases")}>
                  Purchases
                </button>
              </>
            }
          />
        }
        table={
          exchange.data ? (
            <GridTable
              caption="Supplier requests"
              columns={columns}
              rows={shown}
              rowKey={(r) => r.id}
              rowLabel={title}
              sort={sort}
              onSortChange={setSort}
              onRowOpen={(r) => setOpen(r.id)}
              rowAttributes={(r) => ({ "data-request-id": r.id })}
              empty={
                rows.length === 0 ? (
                  <EmptyState
                    icon={Handshake}
                    title="No requests yet"
                    compact
                    testId="requests-empty"
                    action={
                      <EmptyAction
                        label="Request data"
                        allowed={mayCollect}
                        onClick={() => setPanel("request")}
                        testId="requests-empty-ask"
                      />
                    }
                  >
                    Ask a supplier for their emissions data.
                  </EmptyState>
                ) : undefined
              }
              testId="requests-table"
            />
          ) : undefined
        }
        state={
          (exchange.error ?? facilities.error) ? (
            <ReadFailed
              of="The supplier requests"
              error={exchange.error ?? facilities.error}
              onRetry={() => {
                void exchange.refetch();
                void facilities.refetch();
              }}
            />
          ) : !exchange.data ? (
            <GridTable
              caption="Supplier requests"
              columns={[]}
              rows={[]}
              rowKey={(r: Exchange) => r.id}
              loading
              testId="requests-loading"
            />
          ) : undefined
        }
      />
      <Drawer
        open={opened !== undefined}
        onClose={() => setOpen(null)}
        title={opened ? title(opened) : "Request"}
        width="lg"
        testId="supplier-request-drawer"
      >
        {opened ? (
          <ul className={styles.exchange}>
            <ExchangeCard
              row={opened}
              agreements={agreements.data ?? []}
              agreementsFailed={Boolean(agreements.error)}
              agreementsPending={agreements.isPending || agreements.data === undefined}
              onRetryAgreements={() => void agreements.refetch()}
              mayDecide={mayDecide}
              mayCollect={mayCollect}
            />
          </ul>
        ) : null}
      </Drawer>
      <Drawer open={panel === "request"} onClose={close} title="Request data" width="md" testId="supplier-raise-drawer">
        {exchange.data ? <RaiseRequest rows={exchange.data} /> : null}
      </Drawer>
      <Drawer open={panel === "census"} onClose={close} title="Expected bills" width="lg" testId="supplier-census-drawer">
        {/* The census of returned exchanges, in the words the supplier exchange always used: what
            it counts, and what it does not (distinct suppliers, spend coverage, permission). */}
        <SupplierExchangeOverview
          {...(exchange.isError
            ? { status: "failed" as const }
            : exchange.data === undefined
              ? { status: "pending" as const }
              : { status: "ready" as const, rows: exchange.data })}
        />
      </Drawer>
      <Drawer open={panel === "permissions"} onClose={close} title="Permissions" width="lg" testId="supplier-permissions-drawer">
        <PermissionsPanel exchange={exchange.data} agreements={agreements} mayDecide={mayDecide} />
      </Drawer>
      <Drawer open={panel === "responsiveness"} onClose={close} title="Responsiveness" width="lg">
        <SupplierResponsivenessPanel />
      </Drawer>
      <Drawer open={panel === "purchases"} onClose={close} title="Purchases" width="lg">
        <ProcurementPanels mayCollect={mayCollect} mayDecide={mayDecide} />
      </Drawer>
    </>
  );
}
